import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { adminListSupportTickets, adminUpdateSupportTicket, API_BASE_URL } from '../api';

// v9 customer_support_tickets - the website owner's queue of tickets raised from the homepage
// footer's "Raise a ticket" form (replaces the old "Report Damage" button). Platform-wide, so it
// lives behind the admin "More" menu in every vertical.
const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' };
const STATUS_VARIANT = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'secondary' };
const CATEGORIES = ['damage', 'billing', 'booking', 'account', 'other'];
const CATEGORY_LABEL = { damage: 'Damage', billing: 'Billing', booking: 'Booking', account: 'Account', other: 'Other' };

const photoUrl = (u) => (u && u.startsWith('http') ? u : `${API_BASE_URL}${u}`);

function TicketRow({ ticket, onChanged }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const patch = async (payload) => {
    setBusy(true); setErr('');
    try {
      await adminUpdateSupportTicket(ticket._id, payload);
      setNote('');
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.msg || 'Could not update the ticket.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-2">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <Badge bg="secondary" className="me-2">{CATEGORY_LABEL[ticket.category] || ticket.category}</Badge>
          <strong>{ticket.name}</strong>
          <span className="text-muted ms-2 small">
            {ticket.email}{ticket.phone ? ` · ${ticket.phone}` : ''}
            {ticket.orderRef ? ` · order ${ticket.orderRef}` : ''}
          </span>
        </div>
        <span className="small text-muted">{new Date(ticket.createdAt).toLocaleString('en-IN')}</span>
      </div>

      <p className="mb-2 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>

      {ticket.photos?.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {ticket.photos.map((u) => (
            <a key={u} href={photoUrl(u)} target="_blank" rel="noreferrer">
              <img src={photoUrl(u)} alt="attachment" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }} />
            </a>
          ))}
        </div>
      )}

      {ticket.notes?.length > 0 && (
        <div className="small bg-light rounded p-2 mb-2">
          {ticket.notes.map((n, i) => (
            <div key={i}>
              <span className="text-muted">{n.at ? new Date(n.at).toLocaleDateString('en-IN') : ''}: </span>{n.body}
            </div>
          ))}
        </div>
      )}

      {err && <Alert variant="danger" className="py-1 px-2 small">{err}</Alert>}

      <div className="d-flex flex-wrap gap-2 align-items-center">
        <Form.Select
          size="sm" style={{ maxWidth: 160 }} aria-label={`Status for ${ticket.name}`}
          value={ticket.status} disabled={busy}
          onChange={(e) => patch({ status: e.target.value })}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </Form.Select>
        <Badge bg={STATUS_VARIANT[ticket.status] || 'secondary'}>{STATUS_LABEL[ticket.status] || ticket.status}</Badge>
        <Form.Control
          size="sm" className="flex-grow-1" style={{ minWidth: 180 }} placeholder="Add a working note…"
          value={note} disabled={busy} onChange={(e) => setNote(e.target.value)}
        />
        <Button size="sm" variant="outline-secondary" disabled={busy || !note.trim()} onClick={() => patch({ note: note.trim() })}>
          Add note
        </Button>
      </div>
    </div>
  );
}

export default function SupportTicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      setTickets(await adminListSupportTickets(params));
    } catch {
      setError('Could not load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const paged = usePagedArray(tickets, { resetKey: `${statusFilter}:${categoryFilter}` });

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="small text-muted">Status</span>
        <Form.Select size="sm" style={{ maxWidth: 160 }} aria-label="Ticket status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </Form.Select>
        <span className="small text-muted">Category</span>
        <Form.Select size="sm" style={{ maxWidth: 160 }} aria-label="Ticket category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </Form.Select>
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : paged.total === 0 ? (
        <Alert variant="light">No support tickets.</Alert>
      ) : (
        <>
          {paged.rows.map((t) => <TicketRow key={t._id} ticket={t} onChanged={load} />)}
          <Pager {...paged} />
        </>
      )}
    </div>
  );
}
